import { type CSSProperties, type KeyboardEvent } from 'react';
import { makeStyles } from 'tss-react/mui';
import { getColors, typography } from '../tokens';
import SearchIcon from '@mui/icons-material/Search';

export interface SearchBarProps {
  value: string;
  onChange: (value: string) => void;
  onKeyDown?: (e: KeyboardEvent<HTMLInputElement>) => void;
  placeholder?: string;
  style?: CSSProperties;
  className?: string;
}

const useStyles = makeStyles()(theme => {
  const c = getColors(theme.palette.mode);
  const isLight = theme.palette.mode === 'light';
  const fgAlpha = (a: number) =>
    isLight ? `rgba(0,0,0,${a})` : `rgba(255,255,255,${a})`;

  return {
    wrap: {
      display: 'flex',
      alignItems: 'center',
      minHeight: '40px',
      padding: '0 16px',
      gap: 8,
      borderRadius: 9999,
      background: isLight ? '#ffffff' : '#252525',
      width: '100%',
    },
    icon: {
      color: fgAlpha(0.4),
      width: 18,
      height: 18,
      flexShrink: 0,
    },
    input: {
      flex: 1,
      border: 'none',
      outline: 'none',
      background: isLight ? '#ffffff' : '#252525',
      color: c.text.primary,
      fontFamily: typography.fontFamilyCorpoSText,
      fontSize: '0.875rem',
      caretColor: c.text.primary,
      '&::placeholder': {
        color: fgAlpha(0.4),
        opacity: 1,
      },
    },
  };
});

export const SearchBar = ({
  value,
  onChange,
  onKeyDown,
  placeholder = 'Search',
  style,
  className,
}: Readonly<SearchBarProps>) => {
  const { classes } = useStyles();

  const wrapClass = [classes.wrap, className].filter(Boolean).join(' ');

  return (
    <div className={wrapClass} style={style}>
      <SearchIcon className={classes.icon} />
      <input
        type="text"
        className={classes.input}
        placeholder={placeholder}
        value={value}
        onChange={e => onChange(e.target.value)}
        onKeyDown={onKeyDown}
      />
    </div>
  );
};
